/**
 * MÀN HÌNH CHẤM CÔNG — màn hình duy nhất nhân viên dùng mỗi ngày.
 *
 * NGUYÊN TẮC (docs/01 §1.8):
 *   Mọi lần chấm công LUÔN vào hàng đợi TRƯỚC, rồi mới thử gửi.
 *   Nhân viên KHÔNG BAO GIỜ thấy màn hình lỗi mạng —
 *   họ thấy "Đã ghi nhận, đang đồng bộ".
 *
 * Thiết kế: một nút cỡ lớn, bấm được bằng ngón cái khi đeo găng tay ở công
 * trình. Mọi thứ khác là phụ.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import QuetQR from '../components/QuetQR'
import ChupKhuonMat from '../components/ChupKhuonMat'
import { HANH_DONG_LOI, MA_LOI_FACE } from '../api/loi'
import { useTaiDuLieu } from '../hooks/useTaiDuLieu'
import { layChallenge, layLichSu } from '../services/attendance.service'
import { BEACON_URL, layTokenBeacon } from '../services/beacon.service'
import {
  faceDaDangKyCache, layCa, layCongTrinh, layDiaDiem,
} from '../services/catalog.service'
import type { Ca, DiaDiem } from '../types'
// `canFaceTaiViTri` và `laVanPhong` không còn dùng ở đây: từ 12/09/2026 khung
// hình và yêu cầu định vị do NHÂN VIÊN CHỌN quyết định, không suy ra từ GPS.
// Hai hàm vẫn nằm trong `services/catalog.service.ts` cho `TuKiemTra`.
import {
  gio, gioGiay, gioTuSoThuc, ngayCong, ngayDayDu, ngayLui, thuNgan,
} from '../lib/gio'
import { enqueue, flush, pendingCount, type PunchMethod } from '../lib/queue'
import { Icon } from '../ui'
import {
  gioMay, layDeviceId, layViTri, LoiViTri, type ViTri,
} from '../lib/thietbi'

/**
 * Nơi làm việc do CHÍNH NHÂN VIÊN chọn — mô hình hai trường hợp (12/09/2026).
 *
 * PWA không đọc được IP của chính nó nên không tự biết đang ở WiFi công ty hay
 * 4G. Vì vậy luôn hỏi, và để SERVER quyết định: nếu gói tin đi ra từ dải LAN
 * công ty thì tầng `lan_ip` thắng, lựa chọn này thành thừa và vô hại.
 */
type NoiLamViec =
  | { loai: 'van_phong' }
  | { loai: 'cong_trinh'; site_id: number; ten: string }

type TrangThai =
  | { pha: 'san_sang' }
  | { pha: 'chon_noi_lam_viec' }
  | { pha: 'dang_thu_thap'; buoc: string }
  | { pha: 'chua_dang_ky_mat'; viTri: ViTri | null; noi: NoiLamViec }
  | { pha: 'cho_chup_mat'; viTri: ViTri | null; ngoaiVanPhong: boolean; noi: NoiLamViec }
  | { pha: 'da_ghi_nhan'; dong_bo: boolean; luc: Date }
  | { pha: 'can_xu_ly'; thong_diep: string; cho_phep_thu_lai: boolean }

/**
 * Địa chỉ beacon nay khai ở `services/beacon.service.ts` cùng với hàm gọi nó —
 * xem chú thích ở đó. Tóm tắt: CHỈ lấy từ `VITE_BEACON_URL` lúc build, KHÔNG
 * có mặc định, và để trống thì lớp beacon bị bỏ qua hoàn toàn (docs/03 B7 —
 * hạ cấp, không chặn).
 */

/** Ca chọn lần gần nhất. Chỉ là gợi ý cho lần sau, không phải ràng buộc. */
const CA_DA_CHON = 'chamcong.ca_da_chon'

export default function ChamCong() {
  const navigate = useNavigate()
  const [huong, setHuong] = useState<'in' | 'out'>('in')
  const [trangThai, setTrangThai] = useState<TrangThai>({ pha: 'san_sang' })
  const [choDongBo, setChoDongBo] = useState(0)
  const [online, setOnline] = useState(navigator.onLine)
  // Mã 6 số tổ trưởng đọc cho (docs/02 §2.4). Không bắt buộc — có thì +40 điểm.
  const [quet, setQuet] = useState(false)
  const [sessionId, setSessionId] = useState<number | null>(null)
  const [maToTruong, setMaToTruong] = useState('')
  const [hienOMa, setHienOMa] = useState(false)
  // Ca làm việc do admin quản lý, nhân viên chọn mỗi lần chấm công
  // (migration 009). Nhớ lựa chọn lần trước vì đa số ngày người ta làm cùng
  // một ca — nhưng KHÔNG khoá cứng, đổi ca là việc bình thường.
  // Danh sách địa điểm để nhân viên chọn công trình. Cache offline (api.ts),
  // nên chọn được cả khi mất sóng ở công trường.
  const [dsDiaDiem, setDsDiaDiem] = useState<DiaDiem[]>([])
  const [dsCa, setDsCa] = useState<Ca[]>([])
  const [caId, setCaId] = useState<number | null>(() => {
    const luu = localStorage.getItem(CA_DA_CHON)
    return luu ? Number(luu) : null
  })

  // Khôi phục hướng chấm công lần trước: đã Vào ca thì lần sau mặc định là Ra ca
  useEffect(() => {
    const luu = localStorage.getItem('chamcong.huong_ke_tiep')
    if (luu === 'in' || luu === 'out') setHuong(luu)
  }, [])

  useEffect(() => {
    const capNhat = () => { void pendingCount().then(setChoDongBo) }
    const onOnline = () => { setOnline(true); void flush().then(capNhat) }
    const onOffline = () => setOnline(false)

    capNhat()
    const timer = window.setInterval(capNhat, 5_000)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  useEffect(() => {
    void layDiaDiem().then(setDsDiaDiem).catch(() => undefined)
    void layCa().then((ds) => {
      setDsCa(ds)
      // Ca đã chọn trước đây có thể vừa bị admin ngừng dùng — bỏ chọn thay vì
      // gửi lên một id server sẽ từ chối.
      setCaId((cu) => (cu !== null && ds.some((c) => c.id === cu) ? cu : null))
    })
  }, [])

  /**
   * Bấm nút chấm công → hỏi nơi làm việc TRƯỚC.
   *
   * Mô hình 12/09/2026 có đúng hai trường hợp và chúng đòi hai thứ khác nhau:
   * "Văn phòng" bắt buộc định vị, "Công trình" không cần định vị nhưng ảnh
   * phải thấy khuôn viên. Không hỏi trước thì không biết nên xin quyền vị trí
   * hay không, và xin quyền vô cớ ở công trường chỉ làm chậm người đang vội.
   */
  const chamCong = useCallback(() => {
    setTrangThai({ pha: 'chon_noi_lam_viec' })
  }, [])

  const daChonNoi = useCallback(async (noi: NoiLamViec) => {
    let viTri: ViTri | null = null

    if (noi.loai === 'van_phong') {
      // ── VĂN PHÒNG: định vị BẮT BUỘC ────────────────────────────────────
      // Đây là bằng chứng vị trí DUY NHẤT của trường hợp này — không WiFi
      // công ty thì không có `lan_ip`, không có beacon. Thiếu nó thì không
      // còn gì để phân biệt "đang ở văn phòng" với "đang ở nhà".
      //
      // Bán kính 250 m và lưới an toàn tới 500 m (chờ duyệt thay vì từ chối)
      // lo phần GPS trôi trong nhà — xem db/init/012.
      setTrangThai({ pha: 'dang_thu_thap', buoc: 'Đang lấy vị trí…' })
      try {
        viTri = await layViTri()
      } catch (e) {
        if (!(e instanceof LoiViTri)) throw e
        setTrangThai({
          pha: 'can_xu_ly',
          cho_phep_thu_lai: true,
          thong_diep:
            'Chấm công tại văn phòng bằng 4G/5G cần bật định vị. '
            + 'Bật Vị trí (Location) trong Cài đặt rồi thử lại. '
            + 'Nếu đang ở công trình, chọn "Công trình" thay vì "Văn phòng" — '
            + 'khi đó không cần định vị.',
        })
        return
      }
    }
    // ── CÔNG TRÌNH: KHÔNG xin định vị ─────────────────────────────────────
    // Hầm, nhà xưởng, tầng thấp giữa nhà cao tầng — máy không bắt được vệ
    // tinh là chuyện thường. Bằng chứng là ảnh chụp mặt CHUNG KHUNG với khuôn
    // viên công trình, cộng chính lựa chọn này (docs/01 §1.5 tầng 4).

    const ngoaiVanPhong = noi.loai === 'cong_trinh'

    // Chưa đăng ký mặt thì bản ghi chắc chắn vào chờ duyệt. Nhắc trước, nhưng
    // KHÔNG chặn: server mới là nơi quyết định. Cache chưa có (null) → cho chụp.
    if (faceDaDangKyCache() === false) {
      setTrangThai({ pha: 'chua_dang_ky_mat', viTri, noi })
      return
    }
    setTrangThai({ pha: 'cho_chup_mat', viTri, ngoaiVanPhong, noi })
  }, [])

  /** Gom bằng chứng còn lại rồi xếp hàng đợi. Dùng chung cho cả hai nhánh. */
  const hoanTat = useCallback(async (
    viTri: ViTri | null,
    anhMat: Blob | null,
    khaiBao?: { site_id: number | null; ghi_chu: string },
  ) => {
    // ── 3. Beacon: cố lấy token, KHÔNG chặn nếu thất bại ─────────────────
    // Hạ cấp im lặng sang IP + GPS (docs/03 B7). Nhân viên không cần biết.
    let token: string | null = null
    let nonce: string | null = null
    let method: PunchMethod = 'gps_photo'
    const maCa = maToTruong.replace(/\D/g, '')
    if (maCa.length === 6) method = 'qr_supervisor'

    // Chưa cấu hình beacon thì bỏ qua hẳn bước này — không gọi mạng, không
    // chờ timeout, và không đụng tới địa chỉ IP nào của ai.
    if (online && BEACON_URL) {
      setTrangThai({ pha: 'dang_thu_thap', buoc: 'Đang xác thực mạng công ty…' })
      try {
        const ch = await layChallenge()
        nonce = ch.nonce
        token = await layTokenBeacon(BEACON_URL, ch.nonce)
        if (token) method = 'wifi_ip'
      } catch {
        // Không xin được challenge (mất mạng giữa chừng) — vẫn xếp hàng bình thường
      }
    }

    // ── 4. Xếp hàng đợi. Từ đây trở đi KHÔNG THỂ MẤT bản ghi ─────────────
    setTrangThai({ pha: 'dang_thu_thap', buoc: 'Đang ghi nhận…' })
    const clientUuid = await enqueue({
      direction: huong,
      method,
      token,
      nonce,
      location_id: null,          // để server tự dò theo 3 tầng (docs/01 §1.5)
      // Mất GPS không còn là lỗi: server dò địa điểm bằng token beacon, mã tổ
      // trưởng, phân công, rồi tới lời khai kèm ảnh (docs/01 §1.5 tầng 4).
      gps: viTri && { lat: viTri.lat, lng: viTri.lng, accuracy: viTri.accuracy },
      bssid: null,                // PWA thuần không đọc được — xem lib/thietbi.ts
      device_id: layDeviceId(),
      otp_code: maCa.length === 6 ? maCa : null,
      session_id: sessionId,
      photo: anhMat,
      shift_id: caId,
      site_declared_id: khaiBao?.site_id ?? null,
      ghi_chu: khaiBao?.ghi_chu?.trim() || null,
      is_mock_location: viTri?.nghi_gia_mao ?? false,
      is_rooted: false,
      device_clock_ts: gioMay(),
    })
    setMaToTruong('')
    setSessionId(null)
    setHienOMa(false)

    // Đã ghi nhận. Đổi hướng cho lần sau ngay, không chờ server.
    const huongKeTiep = huong === 'in' ? 'out' : 'in'
    setHuong(huongKeTiep)
    localStorage.setItem('chamcong.huong_ke_tiep', huongKeTiep)
    setTrangThai({ pha: 'da_ghi_nhan', dong_bo: false, luc: new Date() })

    // ── 5. Thử gửi ngay. Thất bại cũng không sao — hàng đợi tự thử lại ───
    const kq = await flush()
    void pendingCount().then(setChoDongBo)

    // Cổng Face ID từ chối (ảnh mờ, mặt không khớp, chưa đăng ký…): server đã
    // lưu bản ghi bị từ chối, nhân viên phải chấm LẠI — nói rõ ngay tại chỗ,
    // đừng để họ tưởng đã xong rồi ra về. Đây là lỗi tự sửa được, khác lỗi mạng.
    const loiFace = kq.loi.find((l) => l.client_uuid === clientUuid && MA_LOI_FACE.has(l.code))
    if (loiFace) {
      // Đổi lại hướng: lần này chưa được tính, lần sau vẫn là hướng cũ
      setHuong(huong)
      localStorage.setItem('chamcong.huong_ke_tiep', huong)
      setTrangThai({
        pha: 'can_xu_ly',
        thong_diep: loiFace.huong_dan ?? HANH_DONG_LOI[loiFace.code] ?? loiFace.message,
        cho_phep_thu_lai: true,
      })
      return
    }

    if (kq.synced > 0) {
      setTrangThai({ pha: 'da_ghi_nhan', dong_bo: true, luc: new Date() })
    }
  }, [huong, online, maToTruong, sessionId, caId])

  const vaoCa = huong === 'in'

  /* Bán kính geofence của địa điểm gần nhất — bản thiết kế hiện nó ngay dưới
     ô chọn ca. Chỉ có ý nghĩa khi đã biết vị trí, và chỉ để NÓI CHO BIẾT:
     server mới là nơi quyết định trong hay ngoài phạm vi. */
  const banKinhGanNhat = dsDiaDiem.length > 0 ? dsDiaDiem[0].radius_m : null

  return (
    <div className="man-hinh cham-cong">
      <TrangThaiMang online={online} choDongBo={choDongBo} />

      <div className="dong-ho">
        <DongHo />
      </div>

      {trangThai.pha === 'chon_noi_lam_viec' ? (
        <ChonNoiLamViec
          dsDiaDiem={dsDiaDiem}
          onChon={(noi) => void daChonNoi(noi)}
          onHuy={() => setTrangThai({ pha: 'san_sang' })}
        />
      ) : trangThai.pha === 'cho_chup_mat' ? (
        <ChupCoBoiCanh
          viTri={trangThai.viTri}
          ngoaiVanPhong={trangThai.ngoaiVanPhong}
          onXong={(anh, khaiBao) => void hoanTat(trangThai.viTri, anh,
            khaiBao ?? khaiBaoTuNoi(trangThai.noi))}
          onBoQuaAnh={() => void hoanTat(trangThai.viTri, null,
            khaiBaoTuNoi(trangThai.noi))}
          onHuy={() => setTrangThai({ pha: 'san_sang' })}
        />
      ) : trangThai.pha === 'chua_dang_ky_mat' ? (
        <ChuaDangKyMat
          onDangKy={() => navigate('/ca-nhan')}
          onVanChamCong={() => setTrangThai({
            pha: 'cho_chup_mat', viTri: trangThai.viTri,
            ngoaiVanPhong: trangThai.noi.loai === 'cong_trinh',
            noi: trangThai.noi,
          })}
          onHuy={() => setTrangThai({ pha: 'san_sang' })}
        />
      ) : trangThai.pha === 'da_ghi_nhan' ? (
        <KetQua trangThai={trangThai} onXong={() => setTrangThai({ pha: 'san_sang' })} />
      ) : trangThai.pha === 'can_xu_ly' ? (
        <CanXuLy
          thongDiep={trangThai.thong_diep}
          onThuLai={() => setTrangThai({ pha: 'san_sang' })}
        />
      ) : (
        <button
          type="button"
          className={`nut-cham-cong ${vaoCa ? 'vao-ca' : 'ra-ca'}`}
          onClick={() => void chamCong()}
          disabled={trangThai.pha === 'dang_thu_thap'}
        >
          {trangThai.pha === 'dang_thu_thap' ? (
            <>
              <span className="quay" aria-hidden="true" />
              <span className="nhan-phu">{trangThai.buoc}</span>
            </>
          ) : (
            <>
              <span className="nhan-chinh">{vaoCa ? 'Vào ca' : 'Ra ca'}</span>
              <span className="nhan-phu">Chạm để chấm công</span>
            </>
          )}
        </button>
      )}

      {trangThai.pha === 'san_sang' && dsCa.length > 0 && (
        <div className="o-chon-ca-pill">
          <label>
            <span className="chi-doc-man-hinh">Ca làm việc</span>
            <select
              value={caId ?? ''}
              onChange={(e) => {
                const v = e.target.value ? Number(e.target.value) : null
                setCaId(v)
                if (v === null) localStorage.removeItem(CA_DA_CHON)
                else localStorage.setItem(CA_DA_CHON, String(v))
              }}
            >
              <option value="">— Chưa chọn ca —</option>
              {dsCa.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.start_time}–{c.end_time}
                </option>
              ))}
            </select>
          </label>
          {/* Không chọn ca KHÔNG chặn chấm công (ràng buộc 3). Bản ghi vẫn
              được ghi nhận, chỉ là quản lý phải tự đối chiếu ca. */}
          <p className="ghi-chu-ca">
            {caId === null
              ? 'Chưa chọn ca vẫn chấm công được, nhưng quản lý sẽ phải xác nhận ca giúp bạn.'
              : 'Chọn đúng ca để hệ thống tính giờ vào/ra làm việc.'}
            {banKinhGanNhat !== null && (
              <span className="ban-kinh">( Bán kính phạm vi: {banKinhGanNhat}m )</span>
            )}
          </p>
        </div>
      )}

      {trangThai.pha === 'san_sang' && <TheHomNay />}

      {trangThai.pha === 'san_sang' && (
        hienOMa ? (
          <label className="o-ma-to-truong">
            <span>Mã 6 số của tổ trưởng</span>
            <input
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={7}
              placeholder="123 456"
              value={maToTruong}
              onChange={(e) => { setMaToTruong(e.target.value); setSessionId(null) }}
              autoFocus
            />
            <small>Mã đổi mỗi 30 giây — nhập xong bấm chấm công ngay.</small>
          </label>
        ) : (
          <button type="button" className="lien-ket-phu" onClick={() => setHienOMa(true)}>
            Có mã của tổ trưởng?
          </button>
        )
      )}

      {trangThai.pha === 'san_sang' && (quet
        ? <QuetQR onHuy={() => setQuet(false)} onXong={v => { setSessionId(v.session_id); setMaToTruong(v.code); setHienOMa(true); setQuet(false) }} />
        : <button type="button" className="lien-ket-phu" onClick={() => setQuet(true)}>Quét QR tổ trưởng</button>)}

      {!vaoCa && trangThai.pha === 'san_sang' && (
        <button
          type="button"
          className="lien-ket-phu"
          onClick={() => {
            setHuong('in')
            localStorage.setItem('chamcong.huong_ke_tiep', 'in')
          }}
        >
          Chấm nhầm? Đổi lại thành Vào ca
        </button>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════

/**
 * Thẻ tóm tắt ngày đang xem — bản thiết kế `fe/trang-chu-cham-cong*.pdf`.
 *
 * Trả lời câu hỏi nhân viên hay hỏi nhất khi mở app: *"sáng nay tôi chấm công
 * lúc mấy giờ, hệ thống có nhận không?"* Trước đây phải sang tab khác mới biết.
 *
 * ── Ba nguyên tắc của khối này ────────────────────────────────────────────
 *
 *  1. **Không bao giờ chặn nút chấm công.** Mọi lỗi tải đều nuốt im lặng và
 *     hiện ô trống. Màn hình này tồn tại để bấm một cái nút; một khối phụ trợ
 *     hỏng không được phép đứng chắn (ràng buộc 3 của dự án).
 *
 *  2. **Đọc từ `/attendance/me?month=` của THÁNG đang xem**, cùng endpoint
 *     với tab Chọn tháng. Không endpoint riêng cho "hôm nay", và thêm một
 *     endpoint chỉ để tiết kiệm vài KB là không đáng.
 *
 *  3. **Giờ vào / giờ ra lấy từ chặng `work` đầu và cuối.** Server không trả
 *     riêng hai mốc này cho nhân viên; đây là cách duy nhất dựng lại chúng.
 */
function TheHomNay() {
  const [ngayXem, setNgayXem] = useState(() => ngayCong())

  const thang = ngayXem.slice(0, 7)
  const { du_lieu, dang_tai } = useTaiDuLieu(() => layLichSu(thang), [thang])

  const ngay = du_lieu?.days.find((d) => d.date === ngayXem)
  const chang = ngay?.segments?.filter((s) => s.kind === 'work') ?? []
  const vao = chang[0]?.start
  const ra = chang.at(-1)?.end
  const soGio = ngay?.work_hours ?? 0

  const laHomNay = ngayXem === ngayCong()
  const doiNgay = (buoc: number) => setNgayXem((n) => ngayLui(n, -buoc))

  return (
    <section className="the-hom-nay">
      <div className="the-hom-nay-dau">
        <strong>
          {thuNgan(ngayXem)}, {ngayXem.slice(8)}/{ngayXem.slice(5, 7)}/{ngayXem.slice(0, 4)}
        </strong>
        <div className="the-hom-nay-dieu-khien">
          <button type="button" className="nut-tron-nho" aria-label="Ngày trước"
                  onClick={() => doiNgay(-1)}>
            <Icon ten="trai" size={20} />
          </button>
          <button type="button" className="nut-tron-nho" aria-label="Ngày sau"
                  disabled={laHomNay} onClick={() => doiNgay(1)}>
            <Icon ten="phai" size={20} />
          </button>
          <button type="button" className="nut-tron-nho" aria-label="Về hôm nay"
                  disabled={laHomNay} onClick={() => setNgayXem(ngayCong())}>
            <Icon ten="lich" size={20} />
          </button>
        </div>
      </div>

      <div className="hang-chip">
        <div className={`chip-cong ${vao ? 'co-du-lieu' : ''}`}>
          <span className="chip-nhan">
            Giờ vào
            {vao && <Icon ten="check" size={15} />}
          </span>
          <span className="chip-so">{vao ? gio(vao) : '–:–'}</span>
        </div>

        <div className={`chip-cong ${ra ? 'co-du-lieu' : ''}`}>
          <span className="chip-nhan">
            Giờ ra
            {ra && <Icon ten="check" size={15} />}
          </span>
          <span className="chip-so">{ra ? gio(ra) : '–:–'}</span>
        </div>

        <div className={`chip-cong ${soGio > 0 ? 'tong' : ''}`}>
          <span className="chip-nhan">Công</span>
          <span className="chip-so">{soGio > 0 ? gioTuSoThuc(soGio) : '–'}</span>
        </div>
      </div>

      <div className="the-hom-nay-chan">
        <span>
          {dang_tai && !du_lieu
            ? 'Đang tải…'
            : chang.length === 0
              ? 'Chưa có bản ghi trong ngày này'
              : `${chang.length} chặng làm việc`}
        </span>
        {chang.length > 0 && (
          <span>{chang[0].site_name}</span>
        )}
      </div>
    </section>
  )
}

function TrangThaiMang({ online, choDongBo }: { online: boolean; choDongBo: number }) {
  if (choDongBo > 0) {
    return (
      <div className="bang-trang-thai cho-dong-bo" role="status">
        {choDongBo} bản ghi đang chờ đồng bộ
        {!online && ' · sẽ tự gửi khi có mạng'}
      </div>
    )
  }
  if (!online) {
    return (
      <div className="bang-trang-thai ngoai-tuyen" role="status">
        Không có mạng — vẫn chấm công được bình thường
      </div>
    )
  }
  return <div className="bang-trang-thai truc-tuyen" role="status">Đã kết nối</div>
}

function DongHo() {
  const [bayGio, setBayGio] = useState(new Date())
  useEffect(() => {
    const t = window.setInterval(() => setBayGio(new Date()), 1000)
    return () => window.clearInterval(t)
  }, [])
  return (
    <>
      {/* Luôn hiển thị GMT+7, KHÔNG theo múi giờ máy — xem lib/gio.ts */}
      <div className="gio">{gio(bayGio)}</div>
      <div className="ngay">{ngayDayDu(bayGio)}</div>
      <div className="mui-gio">Giờ Việt Nam (GMT+7)</div>
    </>
  )
}

/**
 * Chụp ảnh xác minh, kèm khai báo công trình khi cần.
 *
 * VĂN PHÒNG      chân dung, không hỏi gì thêm — mạng công ty đã biết bạn ở đâu.
 * NGOÀI VĂN PHÒNG khung rộng để thấy công trình phía sau, kèm hai ô:
 *                 công trình đang làm và việc đang làm.
 *
 * Hai ô đó KHÔNG bắt buộc: có GPS thì server tự xác định địa điểm, lời khai
 * chỉ là thông tin thêm. Chỉ khi mất GPS nó mới thành thứ duy nhất cho biết
 * nhân viên đang ở công trình nào — nên lúc đó mới nhắc rõ.
 */
function ChupCoBoiCanh({
  viTri, ngoaiVanPhong, onXong, onBoQuaAnh, onHuy,
}: {
  viTri: ViTri | null
  ngoaiVanPhong: boolean
  onXong: (anh: Blob, khaiBao: { site_id: number | null; ghi_chu: string }) => void
  onBoQuaAnh: () => void
  onHuy: () => void
}) {
  const [congTrinh, setCongTrinh] = useState<DiaDiem[]>([])
  const [siteId, setSiteId] = useState<number | null>(null)
  const [ghiChu, setGhiChu] = useState('')

  const mucGps = viTri === null

  useEffect(() => {
    if (!ngoaiVanPhong) return
    // Đọc từ cache địa điểm nên chạy được cả khi mất sóng — chính là lúc cần
    void layCongTrinh(viTri).then((ds) => {
      setCongTrinh(ds)
      // Có GPS thì công trình gần nhất là phỏng đoán tốt, điền sẵn cho đỡ thao tác
      if (!mucGps && ds.length > 0) setSiteId(ds[0].id)
    })
  }, [ngoaiVanPhong, viTri, mucGps])

  return (
    <ChupKhuonMat
      cheDo={ngoaiVanPhong ? 'toan_khung' : 'chan_dung'}
      tieuDe={ngoaiVanPhong ? 'Chụp ảnh tại công trình' : 'Xác minh khuôn mặt'}
      huongDan={ngoaiVanPhong
        ? 'Lùi lại để thấy cả khuôn mặt bạn và công trình phía sau trong một khung.'
        : 'Đưa khuôn mặt vào giữa vòng tròn. Bỏ khẩu trang và kính râm.'}
      onXong={(anh) => onXong(anh, { site_id: siteId, ghi_chu: ghiChu })}
      onBoQuaAnh={onBoQuaAnh}
      onHuy={onHuy}
      duoiKhung={ngoaiVanPhong && (
        <div className="khai-bao-cong-trinh">
          {mucGps && (
            <p className="canh-bao-nhe" role="status">
              Máy không lấy được vị trí. Hãy chọn đúng công trình bên dưới —
              đây là căn cứ để tính công cho bạn.
            </p>
          )}
          <label>
            <span>Công trình đang làm</span>
            <select
              value={siteId ?? ''}
              onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">— Chọn công trình —</option>
              {congTrinh.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Việc đang làm</span>
            <input
              type="text"
              maxLength={200}
              placeholder="Ví dụ: Đổ bê tông tầng 3"
              value={ghiChu}
              onChange={(e) => setGhiChu(e.target.value)}
            />
          </label>
        </div>
      )}
    />
  )
}

/**
 * Màn hình sau khi chấm công.
 *
 * Cố ý KHÔNG hiển thị điểm tin cậy hay trạng thái `needs_review`: nhân viên
 * không kiểm soát được những thứ đó, hiện lên chỉ gây lo lắng vô ích. Bản ghi
 * chờ duyệt vẫn được quản lý xử lý, và tự động duyệt sau 24 giờ (docs/02 §2.8).
 */
function KetQua({
  trangThai, onXong,
}: {
  trangThai: { dong_bo: boolean; luc: Date }
  onXong: () => void
}) {
  useEffect(() => {
    const t = window.setTimeout(onXong, 6000)
    return () => window.clearTimeout(t)
  }, [onXong])

  return (
    <div className="ket-qua" role="status">
      <div className="dau-tich" aria-hidden="true">✓</div>
      <div className="ket-qua-tieu-de">Đã ghi nhận</div>
      <div className="ket-qua-gio">{gioGiay(trangThai.luc)}</div>
      <div className="ket-qua-phu">
        {trangThai.dong_bo
          ? 'Đã gửi lên hệ thống · giờ ghi nhận lấy từ máy chủ'
          : 'Đang đồng bộ…'}
      </div>
      <button type="button" className="lien-ket-phu" onClick={onXong}>
        Xong
      </button>
    </div>
  )
}

/** Lựa chọn nơi làm việc → lời khai gửi lên server. */
function khaiBaoTuNoi(noi: NoiLamViec): { site_id: number | null; ghi_chu: string } {
  // Văn phòng KHÔNG khai `site_id`: server tự xác định bằng `lan_ip` (WiFi
  // công ty) hoặc geofence GPS. Khai vào chỉ khiến tầng "lời khai" thắng và
  // bản ghi rơi xuống chờ duyệt một cách vô cớ.
  return noi.loai === 'cong_trinh'
    ? { site_id: noi.site_id, ghi_chu: '' }
    : { site_id: null, ghi_chu: '' }
}

/**
 * BƯỚC ĐẦU TIÊN: bạn đang làm ở đâu? — mô hình hai trường hợp (12/09/2026).
 *
 * Vì sao hỏi thay vì tự đoán: PWA không đọc được IP của chính nó nên không
 * biết đang ở WiFi công ty hay 4G, và GPS thì không đáng tin trong nhà. Hai
 * lựa chọn dưới đây đòi hai thứ khác nhau — văn phòng cần định vị, công trình
 * cần ảnh thấy khuôn viên — nên phải biết trước mới xin đúng quyền.
 *
 * Ở WiFi công ty thì lựa chọn này vô hại: server thấy IP thuộc dải LAN và
 * tầng `lan_ip` thắng, không cần tới lời khai.
 */
function ChonNoiLamViec({
  dsDiaDiem, onChon, onHuy,
}: {
  dsDiaDiem: DiaDiem[]
  onChon: (noi: NoiLamViec) => void
  onHuy: () => void
}) {
  const congTrinh = dsDiaDiem.filter((d) => d.kind === 'site')
  const [moCongTrinh, setMoCongTrinh] = useState(false)

  return (
    <div className="chon-noi-lam-viec">
      <h2>Bạn đang làm ở đâu?</h2>

      {!moCongTrinh && (
        <>
          <button type="button" className="nut-chon-noi"
                  onClick={() => onChon({ loai: 'van_phong' })}>
            <span className="nut-chon-nhan">Văn phòng</span>
            <span className="nut-chon-mo-ta">Cần bật định vị và chụp khuôn mặt</span>
          </button>

          <button type="button" className="nut-chon-noi"
                  onClick={() => setMoCongTrinh(true)}
                  disabled={congTrinh.length === 0}>
            <span className="nut-chon-nhan">Công trình</span>
            <span className="nut-chon-mo-ta">
              {congTrinh.length === 0
                ? 'Chưa có công trình nào được khai báo'
                : 'Không cần định vị. Chụp khuôn mặt cùng khuôn viên công trình'}
            </span>
          </button>
        </>
      )}

      {moCongTrinh && (
        <>
          <p className="ghi-chu">Chọn công trình bạn đang làm:</p>
          <div className="danh-sach-cong-trinh">
            {congTrinh.map((d) => (
              <button key={d.id} type="button" className="nut-chon-noi"
                      onClick={() => onChon({ loai: 'cong_trinh', site_id: d.id, ten: d.name })}>
                <span className="nut-chon-nhan">{d.name}</span>
              </button>
            ))}
          </div>
          <button type="button" className="nut-phu" onClick={() => setMoCongTrinh(false)}>
            ← Quay lại
          </button>
        </>
      )}

      <button type="button" className="nut-phu" onClick={onHuy}>Huỷ</button>
    </div>
  )
}

/**
 * Công trình bắt buộc Face ID nhưng nhân viên chưa đăng ký mặt.
 *
 * Không chặn — vẫn cho chấm, bản ghi sẽ chờ quản lý duyệt (docs/07 §7.5).
 * Nhưng nói trước để họ đăng ký một lần rồi khỏi phải chờ duyệt mỗi ngày.
 */
function ChuaDangKyMat({
  onDangKy, onVanChamCong, onHuy,
}: {
  onDangKy: () => void
  onVanChamCong: () => void
  onHuy: () => void
}) {
  return (
    <div className="can-xu-ly" role="alert">
      <div className="can-xu-ly-tieu-de">Chưa đăng ký khuôn mặt</div>
      <p className="can-xu-ly-noi-dung">
        Công trình này bắt buộc xác minh khuôn mặt. Bạn chưa đăng ký nên bản
        ghi hôm nay sẽ phải chờ quản lý duyệt. Đăng ký một lần (mất khoảng 1
        phút) để các lần sau được xác nhận ngay.
      </p>
      <button type="button" className="nut-thu-lai" onClick={onDangKy}>
        Đăng ký khuôn mặt
      </button>
      <button type="button" className="lien-ket-phu" onClick={onVanChamCong}>
        Vẫn chấm công, chờ quản lý duyệt
      </button>
      <button type="button" className="lien-ket-phu" onClick={onHuy}>
        Huỷ
      </button>
    </div>
  )
}

/**
 * Chỉ dùng cho lỗi NHÂN VIÊN TỰ SỬA ĐƯỢC (chưa bật GPS, chưa cấp quyền,
 * ảnh mờ, mặt không khớp). Lỗi mạng KHÔNG BAO GIỜ dẫn tới đây — hàng đợi lo.
 */
function CanXuLy({
  thongDiep, onThuLai,
}: {
  thongDiep: string
  onThuLai: () => void
}) {
  return (
    <div className="can-xu-ly" role="alert">
      <div className="can-xu-ly-tieu-de">Cần xử lý</div>
      <p className="can-xu-ly-noi-dung">{thongDiep}</p>
      <button type="button" className="nut-thu-lai" onClick={onThuLai}>
        Thử lại
      </button>
    </div>
  )
}
